<template lang="pug">
  CommonSection.b-specification-page(
    title="Спецификация"
    data-testid="spec-page"
    :back="{ title: project && project.name, name: 'project-stages', params: { projectId } }"
    )
    template(v-if="specification")
      ._toolbar
        Btn(blue sm data-testid="spec-import" v-if="!readOnly" @click="openImport") Импорт
        Btn(blue sm data-testid="spec-add-item" v-if="!readOnly" @click="addItem") + Позиция
        Btn(grey sm data-testid="spec-budget" v-if="!readOnly" @click="runBudget") Плановый бюджет
        Btn(grey sm data-testid="spec-find-photos" v-if="!readOnly" @click="runPhotos") Найти фото
        ._export
          Btn(grey sm data-testid="spec-export" @click="exportOpen = !exportOpen") Экспорт ▾
          SpecPopover(v-if="exportOpen")
            a.spec-popover-item(data-testid="spec-export-designer-xlsx" :href="exportUrl('xlsx', 'designer')" @click="exportOpen = false") Таблица дизайнера, xlsx
            a.spec-popover-item(data-testid="spec-export-designer-pdf" :href="exportUrl('pdf', 'designer')" @click="exportOpen = false") Таблица дизайнера, pdf
            a.spec-popover-item(data-testid="spec-export-customer-xlsx" :href="exportUrl('xlsx', 'customer')" @click="exportOpen = false") Таблица заказчика, xlsx
            a.spec-popover-item(data-testid="spec-export-customer-pdf" :href="exportUrl('pdf', 'customer')" @click="exportOpen = false") Таблица заказчика, pdf
        Btn(grey sm data-testid="spec-requests" @click="openRequests") Запросы
        Btn(grey sm data-testid="spec-settings" @click="openSettings") Настройки
        Btn(grey sm data-testid="spec-history" @click="historyOpen = true") История

      ._filters
        ._grouping
          button._tab(type="button" data-testid="spec-group-by-category" :class="{ active: grouping === 'category' }" @click="grouping = 'category'") Категории
          button._tab(type="button" data-testid="spec-group-by-room" :class="{ active: grouping === 'room' }" @click="grouping = 'room'") Помещения
        select._filter(data-testid="spec-filter-category" v-model="filters.category")
          option(value="") все категории
          option(v-for="(title, code) in CATEGORIES" :key="code" :value="code") {{ title }}
        select._filter(data-testid="spec-filter-room" v-model="filters.room")
          option(value="") все помещения
          option(v-for="room in rooms" :key="room" :value="room") {{ room }}
        select._filter(data-testid="spec-filter-status" v-model="filters.status")
          option(value="") все статусы
          option(v-for="(title, code) in ITEM_STATUSES" :key="code" :value="code") {{ title }}
        select._filter(data-testid="spec-filter-supplier" v-model="filters.supplier")
          option(value="") все поставщики
          option(v-for="supplier in supplierOptions" :key="supplier" :value="supplier") {{ supplier }}
        select._filter(v-model="deviationSort" @change="onDeviationSort")
          option(value="") сортировка
          option(value="over") Перерасход сверху
          option(value="save") Экономия сверху

      SpecAiIndicator(
        :jobs="jobs"
        :readOnly="readOnly"
        @result="onJobResult"
        @retry="retryFailed"
        @cancel="cancelJob"
        @dismiss="dismissJob"
        )
      SpecSummary(v-if="summary" ref="summary" :summary="summary")
      SpecBudgetBar(
        v-if="budget && (budget.limit !== null || budget.analogCount || filters.analogs)"
        :budget="budget"
        :analogsOnly="filters.analogs"
        @toggle-analogs="filters.analogs = !filters.analogs"
        )
      SpecBulkPanel(
        v-if="!readOnly && selectedIds.length"
        :count="selectedIds.length"
        :rooms="rooms"
        @move-category="bulkCategory"
        @set-room="bulkRoom"
        @request="openRequestDialog(selectedItems)"
        @auto-choose="bulkAutoChoose"
        @budget="runBudget"
        @find-photos="runPhotos"
        @delete="bulkDelete"
        )

      ._empty(v-if="!items.length" data-testid="spec-empty") Позиций пока нет — импортируйте файл или добавьте вручную
      SpecTable(
        v-if="items.length || newRowOpen"
        ref="table"
        :groups="groups"
        :rooms="rooms"
        :selectedIds="selectedIds"
        :unlockedIds="unlockedIds"
        :specificationId="specification.id"
        :grouping="grouping"
        :readOnly="readOnly"
        :stickyTop="summaryHeight"
        @select="onSelect"
        @select-group="onSelectGroup"
        @save="onSave"
        @create-item="onCreateItem"
        @transition="onTransition"
        @action="onRowAction"
        @photo-file="onPhotoFile"
        @choose="onChoose"
        @change-split="onChangeSplit"
        @rework="onOfferRework"
        @open-chat="onOfferChat"
        @reorder="onReorder"
        @move-room="onMoveRoom"
        @reorder-categories="onReorderCategories"
        @sort="onSort"
        )

      SpecRequestsPanel(
        v-if="requestsOpen"
        :requests="requests"
        :readOnly="readOnly"
        @close="requestsOpen = false"
        @reworked="onReworked"
        @goto="gotoItem"
        )
      SpecHistoryPanel(
        v-if="historyOpen"
        :specificationId="specification.id"
        :readOnly="readOnly"
        @close="historyOpen = false"
        @reimport="onReimport"
        )
      SpecChatDrawer(
        v-if="chatDrawer"
        :item="chatDrawer.item"
        :chats="chatDrawer.chats"
        :readOnly="readOnly"
        @close="chatDrawer = null"
        @goto-item="onChatGotoItem"
        @goto-request="onChatGotoRequest"
        )
</template>

<script>
import CommonSection from '@/components/CommonSection.vue';
import SpecTable from '@/components/specification/SpecTable.vue';
import SpecSummary from '@/components/specification/SpecSummary.vue';
import SpecBudgetBar from '@/components/specification/SpecBudgetBar.vue';
import SpecAiIndicator from '@/components/specification/SpecAiIndicator.vue';
import SpecBulkPanel from '@/components/specification/SpecBulkPanel.vue';
import SpecRequestsPanel from '@/components/specification/SpecRequestsPanel.vue';
import SpecHistoryPanel from '@/components/specification/SpecHistoryPanel.vue';
import SpecPopover from '@/components/specification/SpecPopover.vue';
import SpecImportPopupWrappable from '@/components/specification/SpecImportPopupWrappable.vue';
import SpecRequestPopupWrappable from '@/components/specification/SpecRequestPopupWrappable.vue';
import SpecSplitPopupWrappable from '@/components/specification/SpecSplitPopupWrappable.vue';
import SpecSettingsPopupWrappable from '@/components/specification/SpecSettingsPopupWrappable.vue';
import SpecPhotosPopupWrappable from '@/components/specification/SpecPhotosPopupWrappable.vue';
import SpecBudgetPopupWrappable from '@/components/specification/SpecBudgetPopupWrappable.vue';
import SpecAutoChoosePopupWrappable from '@/components/specification/SpecAutoChoosePopupWrappable.vue';
import SpecChatDrawer from '@/components/specification/SpecChatDrawer.vue';

import model from '@/model/specification';
import specChat from '@/model/specChat';
import popup from '@/model/popup';
import { apiRun } from '@/model/apiRun';
import specificationApi from '@/api/specification';
import specRequestApi from '@/api/specRequest';
import projectApi from '@/api/project';
import {
  CATEGORIES, ITEM_STATUSES, categoryTitle, projectRooms, summarize, toNumber, idOf,
} from '@/utils/spec';

const NO_ROOM = 'Без помещения';

/** Статусы, в которых отклик можно выбрать — и вручную, и автовыбором (ТЗ 2.5). */
const AUTO_CHOOSE_STATUSES = ['has_offers', 'customer_rejected', 'customer_review'];

/** Нулевые итоги группы, пока свод по ней ещё не пришёл с сервера. */
const EMPTY_TOTAL = {
  plannedTotal: 0,
  customerTotal: 0,
  planDeviation: 0,
  savingsTotal: 0,
  rewardTotal: 0,
  commissionTotal: 0,
  designerIncome: 0,
};

/**
 * Вкладка «Спецификация» (ТЗ 6.2). Страница владеет фильтрами, сортировкой, группировкой,
 * выделением и всеми окнами; таблица, свод и панели — отдельные компоненты.
 *
 * Свод спецификации и итоги категорий приходят готовыми в ответе спецификации (ТЗ 4.1, 7.2) и
 * берутся оттуда; «пересчёт на лету» из 6.2 — это перечитывание спецификации после правок,
 * которые меняют суммы (см. model/specification). Клиент складывает сам только итоги
 * групп-помещений: такой разбивки в своде сервера нет.
 *
 * Свод показывает всю спецификацию и за фильтрами не следует (решение аналитика, этап 7).
 * Сгенерировано Claude.
 */
export default {
  name: 'SpecificationPage',
  components: {
    CommonSection,
    SpecTable,
    SpecSummary,
    SpecBudgetBar,
    SpecAiIndicator,
    SpecBulkPanel,
    SpecRequestsPanel,
    SpecHistoryPanel,
    SpecChatDrawer,
    SpecPopover,
  },
  props: {
    project: Object,
  },
  data() {
    return {
      CATEGORIES,
      ITEM_STATUSES,
      grouping: 'category',
      filters: { category: '', room: '', status: '', supplier: '', analogs: false },
      sort: null,
      deviationSort: '',
      selectedIds: [],
      unlockedIds: [],
      requests: [],
      requestsOpen: false,
      historyOpen: false,
      /** Шторка «Чат по позиции»: позиция и её чаты, или null — шторка закрыта. */
      chatDrawer: null,
      exportOpen: false,
      /** Открыта строка новой позиции в пустой спецификации (групп ещё нет). */
      newRowOpen: false,
      /** Высота закреплённого свода: под ним закрепляется шапка таблицы. */
      summaryHeight: 0,
    };
  },
  computed: {
    projectId() { return Number(this.$route.params.projectId); },
    specification() { return this.$store.state.specification.specification; },
    jobs() { return this.$store.state.specification.jobs; },
    /**
     * Архивный проект — режим только чтения: один признак на все действия записи (ТЗ 6.1, 6.9).
     */
    readOnly() { return !!this.project?.isArchived; },
    rooms() { return projectRooms(this.project); },
    items() { return this.specification?.items ?? []; },
    supplierOptions() {
      return [...new Set(this.items.map((i) => i.supplierName).filter((n) => !!n))];
    },
    filtered() {
      return this.items.filter((item) => {
        if (this.filters.category && item.category !== this.filters.category) return false;
        if (this.filters.room && item.room !== this.filters.room) return false;
        if (this.filters.status && item.status !== this.filters.status) return false;
        if (this.filters.analogs && !item.plannedPriceAnalog) return false;
        if (this.filters.supplier) {
          const own = (item.offers ?? []).some((o) => o.supplier?.name === this.filters.supplier);
          if (item.supplierName !== this.filters.supplier && !own) return false;
        }
        return true;
      });
    },
    /** Свод спецификации целиком — как его посчитал сервер (ТЗ 7.2). */
    summary() { return this.$store.getters['specification/summary']; },
    hasSummary() { return !!this.summary; },
    budget() { return this.$store.getters['specification/budget']; },
    /** Итоги по категориям из того же свода (ТЗ 7.2). */
    categorySummary() { return this.$store.getters['specification/categorySummary']; },
    selectedItems() {
      return this.items.filter((item) => this.selectedIds.includes(item.id));
    },
    /** Группы таблицы: категории в порядке спецификации или помещения в порядке проекта. */
    groups() {
      if (this.grouping === 'room') {
        // разбивки по помещениям в своде сервера нет — итоги таких групп складываются здесь
        return [...this.rooms, NO_ROOM].map((room) => {
          const items = this.sortItems(this.filtered.filter(
            (item) => (room === NO_ROOM ? !item.room : item.room === room),
          ));
          return {
            key: `room-${room}`,
            title: room,
            room: room === NO_ROOM ? null : room,
            items,
            total: summarize(items),
          };
        });
      }

      const order = this.specification?.categoryOrder ?? Object.keys(CATEGORIES);
      return order
        .map((category) => ({
          key: `cat-${category}`,
          title: categoryTitle(category),
          category,
          items: this.sortItems(this.filtered.filter((item) => item.category === category)),
          total: this.categorySummary[category] ?? EMPTY_TOTAL,
        }))
        .filter((group) => group.items.length > 0);
    },
    /** Порядок позиций на экране — он же уходит в выгрузку (ТЗ 4.1). */
    visibleIds() {
      return this.groups.reduce((acc, group) => [...acc, ...group.items.map((i) => i.id)], []);
    },
  },
  watch: {
    projectId: {
      immediate: true,
      handler() { this.load(); },
    },
    /** Переход «к позиции» приходит адресом: параметр `item` раскрывает строку (ТЗ 6.2, 6.8). */
    '$route.query.item': function watchRouteItem(value) {
      if (value) this.expandRouteItem(Number(value));
    },
    /** Свод появляется после загрузки: тогда и начинаем следить за его высотой. */
    hasSummary: {
      immediate: true,
      handler() { this.$nextTick(this.observeSummary); },
    },
  },
  beforeDestroy() {
    model.stopPolling();
    this.summaryObserver?.disconnect();
  },
  methods: {
    /**
     * Высота свода меняется (перенос строк на узком окне), поэтому она отслеживается, а не
     * задаётся константой: иначе закреплённая шапка таблицы залезала бы под свод.
     */
    observeSummary() {
      this.summaryObserver?.disconnect();
      const el = this.$refs.summary?.$el;
      if (!el) {
        this.summaryHeight = 0;
        return;
      }
      this.summaryHeight = el.offsetHeight;
      if (typeof ResizeObserver === 'undefined') return;
      this.summaryObserver = new ResizeObserver(() => { this.summaryHeight = el.offsetHeight; });
      this.summaryObserver.observe(el);
    },
    async load() {
      const specification = await model.fetch(this.projectId);
      await model.restoreActiveJobs(specification.id);
      this.openItemFromRoute();
    },
    sortItems(items) {
      const list = [...items];
      if (!this.sort) return list.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
      const { field, desc } = this.sort;
      const value = (item) => {
        // столбец «Отклики» сортируется по их числу, а не по самому списку
        if (field === 'offers') return (item.offers ?? []).length;
        const raw = item[field];
        const number = toNumber(raw);
        return number === null ? String(raw ?? '') : number;
      };
      return list.sort((a, b) => {
        const x = value(a);
        const y = value(b);
        if (x === y) return 0;
        return (x > y ? 1 : -1) * (desc ? -1 : 1);
      });
    },
    onSort(field) {
      this.deviationSort = '';
      this.sort = this.sort && this.sort.field === field && !this.sort.desc
        ? { field, desc: true }
        : { field, desc: false };
    },
    /** Два пункта меню сортировки: «Перерасход сверху» и «Экономия сверху» (ТЗ 6.2). */
    onDeviationSort() {
      if (!this.deviationSort) {
        this.sort = null;
        return;
      }
      this.sort = { field: 'planDeviation', desc: this.deviationSort === 'over' };
    },

    onSelect({ item, value }) {
      this.selectedIds = value
        ? [...new Set([...this.selectedIds, item.id])]
        : this.selectedIds.filter((id) => id !== item.id);
    },
    onSelectGroup({ group, value }) {
      const ids = group.items.map((i) => i.id);
      this.selectedIds = value
        ? [...new Set([...this.selectedIds, ...ids])]
        : this.selectedIds.filter((id) => !ids.includes(id));
    },

    onSave({ item, field, value }) {
      model.patchItem(item.id, { [field]: value === '' ? null : value });
    },
    async onCreateItem({ name, category, room }) {
      this.newRowOpen = false;
      await model.createItem({
        specification: `/api/specifications/${this.specification.id}`,
        name,
        category,
        room: room ?? null,
        quantity: '1',
        unit: 'pcs',
      });
    },
    /**
     * «+ Позиция»: строка новой позиции в первой группе, а в пустой спецификации (групп ещё нет) —
     * отдельная строка с выбором категории; на сервер она уходит только после выбора (ТЗ 6.2).
     */
    addItem() {
      const [group] = this.groups;
      if (group) {
        this.$refs.table.startNewRow(group);
        return;
      }
      this.newRowOpen = true;
      this.$nextTick(() => this.$refs.table.startNewRow(null));
    },
    async onTransition({ item, action }) {
      await model.transition(item, action);
    },
    async onRowAction({ item, action }) {
      switch (action) {
        case 'request':
          this.openRequestDialog([item]);
          break;
        case 'find-price':
          await model.findPrice(item.id);
          break;
        case 'find-photo':
          await this.startJob('images', [item.id]);
          break;
        case 'chat':
          await this.openChatFor(item);
          break;
        case 'edit':
          await this.unlockAccepted(item);
          break;
        case 'reset':
          await model.resetItem(item);
          break;
        case 'analog-apply':
          await model.decideAnalog(item, 'apply');
          break;
        case 'analog-reject':
          await model.decideAnalog(item, 'reject');
          break;
        case 'delete':
          if (await model.deleteItem(item)) this.selectedIds = this.selectedIds.filter((id) => id !== item.id);
          break;
        case 'photo-enlarge':
          if (item.photo) popup.addPreview([{ type: 'image', src: item.photo.url }]);
          break;
        default:
          break;
      }
    },
    /**
     * «Открыть чат» из меню строки (ТЗ 6.2): шторка справа, со страницы не уходим. Собеседники —
     * заказчик и поставщики, в чьи запросы КП входит эта позиция. Чаты уже существуют
     * (двусторонние создаёт сервер), поэтому берём их список и отбираем нужные.
     */
    async openChatFor(item, supplierId = null) {
      const [chats] = await Promise.all([
        specChat.fetchDesignerChats(this.specification.id),
        this.reloadRequests(),
      ]);

      const supplierIds = this.requests
        .filter((request) => (request.items ?? []).some((row) => row.item?.id === item.id))
        .map((request) => idOf(request.supplier))
        .filter((id) => !!id);

      const itemChats = chats.filter((chat) => (chat.kind === 'customer' ? true : supplierIds.includes(idOf(chat.supplier))));
      const preferred = supplierId
        ? itemChats.find((chat) => chat.kind === 'supplier' && idOf(chat.supplier) === supplierId)
        : null;
      this.chatDrawer = {
        item,
        // чат с поставщиком отклика — первым, его шторка и откроет
        chats: preferred ? [preferred, ...itemChats.filter((chat) => chat !== preferred)] : itemChats,
      };
    },
    /** «чат» у поставщика в таблице откликов (ТЗ 6.2): шторка сразу на чате с этим поставщиком. */
    onOfferChat({ item, offer }) {
      return this.openChatFor(item, idOf(offer.supplier));
    },
    /** «К позиции» из события в шторке чата: шторка закрывается, строка раскрывается. */
    onChatGotoItem(item) {
      this.chatDrawer = null;
      this.gotoItem(item);
    },
    async onChatGotoRequest() {
      this.chatDrawer = null;
      await this.openRequests();
    },
    /**
     * Единый переход «к позиции» (ТЗ 6.2, 6.10, 6.8): спецификация открывается с раскрытой
     * строкой. Тот же механизм — по адресу с параметром `item` (ссылка из уведомления, событие
     * чата, панель «Запросы»).
     */
    gotoItem(item) {
      const id = typeof item === 'object' ? item?.id : Number(item);
      if (!id) return;
      this.requestsOpen = false;
      if (Number(this.$route.query.item) !== id) {
        this.$router.replace({
          name: 'project-specification',
          params: { projectId: this.projectId },
          query: { ...this.$route.query, item: String(id) },
        });
        return;
      }
      this.expandRouteItem(id);
    },
    /** Адрес может просить раскрыть позицию или открыть панель «Запросы» (ТЗ 6.8, 6.10). */
    openItemFromRoute() {
      const id = Number(this.$route.query.item);
      if (id) this.expandRouteItem(id);
      if (this.$route.query.requests) this.openRequests();
    },
    expandRouteItem(id) {
      this.$nextTick(() => {
        const { table } = this.$refs;
        if (table) table.expandItem(id);
      });
    },
    async unlockAccepted(item) {
      const answer = await popup.addConfirm(
        'Позиция принята, изменение будет записано в историю',
        'Ячейки откроются на правку до ухода со страницы.',
      );
      if (!answer) return;
      this.unlockedIds = [...this.unlockedIds, item.id];
    },
    onPhotoFile({ item, file }) {
      model.uploadPhoto(item.id, file);
    },

    /** Пакетные действия (ТЗ 4.1): одна операция, ответ — вся спецификация со сводом. */
    bulkCategory(category) {
      model.batch(this.specification.id, {
        action: 'move_category', ids: this.selectedIds, category,
      });
    },
    bulkRoom(room) {
      model.batch(this.specification.id, { action: 'set_room', ids: this.selectedIds, room });
    },
    async bulkDelete() {
      const answer = await popup.addConfirm(
        `Удалить позиции: ${this.selectedIds.length}?`,
        'Вместе с позициями удалятся их отклики; поставщики незавершённых запросов получат уведомление.',
      );
      if (!answer) return;
      await model.batch(this.specification.id, { action: 'delete', ids: this.selectedIds });
      this.selectedIds = [];
    },
    /**
     * Автовыбор откликов по отмеченным позициям: режим и признак — в окне, выбор делает сервер.
     * Итог считается сравнением позиций до и после: ответ пакетного действия — вся спецификация.
     */
    async bulkAutoChoose() {
      const params = await popup.add({
        wrappable: true,
        component: SpecAutoChoosePopupWrappable,
        params: { count: this.selectedIds.length },
      });
      if (!params) return;

      const before = this.selectedItems.map((item) => ({ ...item }));
      const specification = await model.batch(this.specification.id, {
        action: 'auto_choose', ids: this.selectedIds, ...params,
      });
      if (!specification) return;

      const after = new Map((specification.items ?? []).map((item) => [item.id, item]));
      const counts = {
        chosen: 0, split: 0, kept: 0, alreadyChosen: 0, unavailable: 0,
      };
      before.forEach((old) => {
        const item = after.get(old.id);
        const oldOffer = idOf(old.chosenOffer);
        if (item && idOf(item.chosenOffer) !== oldOffer) counts.chosen += 1;
        else if (item && toNumber(item.customerDiscount) !== toNumber(old.customerDiscount)) counts.split += 1;
        else if (!AUTO_CHOOSE_STATUSES.includes(old.status)) counts.unavailable += 1;
        else if (oldOffer && !params.includeChosen) counts.alreadyChosen += 1;
        else if (oldOffer) counts.kept += 1;
        else counts.unavailable += 1;
      });

      const lines = [
        `Выбран отклик: ${counts.chosen}`,
        counts.split && `изменено только разделение скидки: ${counts.split}`,
        counts.kept && `выбор уже соответствует режиму: ${counts.kept}`,
        counts.alreadyChosen && `пропущено, отклик уже выбран: ${counts.alreadyChosen}`,
        counts.unavailable && `пропущено, нет новых откликов или позиция уже утверждена: ${counts.unavailable}`,
      ].filter((line) => !!line);
      this.selectedIds = [];
      await popup.addAlert('Автовыбор откликов выполнен', `${lines.join('; ')}.`);
    },
    onReorder({ ids }) {
      model.batch(this.specification.id, { action: 'reorder', ids });
    },
    onMoveRoom({ item, room }) {
      model.batch(this.specification.id, { action: 'set_room', ids: [item.id], room });
    },
    onReorderCategories({ from, to }) {
      const order = [...(this.specification.categoryOrder ?? Object.keys(CATEGORIES))];
      const fromIndex = order.indexOf(from);
      if (fromIndex < 0 || !order.includes(to)) return;
      order.splice(fromIndex, 1);
      // место назначения считается уже без перетаскиваемой категории, иначе при переносе
      // вверх она встала бы после цели
      order.splice(order.indexOf(to), 0, from);
      model.setCategoryOrder(this.specification.id, order);
    },

    /** Задания AI (ТЗ 5.1): тип, позиции, регион проекта. */
    startJob(type, itemIds, extra = {}) {
      return model.startJob({
        specification: `/api/specifications/${this.specification.id}`,
        type,
        input: { itemIds, ...extra },
      });
    },
    async runBudget() {
      const ids = this.selectedIds.length
        ? [...this.selectedIds]
        : this.items.map((item) => item.id);
      if (!ids.length) return;

      // окно открывается при каждом запуске: порог цены и уточнения для AI; регион — если его нет в проекте
      let region = this.project?.region ?? '';
      const answer = await popup.add({
        wrappable: true,
        component: SpecBudgetPopupWrappable,
        params: {
          needRegion: !region,
          budgetLimit: this.specification.budgetLimit,
          budgetHint: this.specification.budgetHint,
        },
      });
      if (!answer) return;

      if (!region) {
        region = answer.region;
        await projectApi.update(this.projectId, { region });
        this.$store.dispatch('project/fetchProject', { projectId: this.projectId, force: true });
      }
      const saved = await model.setBudgetSettings(this.specification.id, answer);
      if (!saved) return;
      await this.startJob('budget', ids, { region });
    },
    async runPhotos() {
      const source = this.selectedIds.length ? this.selectedItems : this.items;
      const ids = source.filter((item) => !item.photo).map((item) => item.id);
      if (!ids.length) return;
      await this.startJob('images', ids);
    },
    async retryFailed(job) {
      const ids = model.failedItemIds(job);
      if (!ids.length) return;
      await this.startJob(job.type, ids);
    },
    onJobResult(job) {
      if (job.type === 'import') {
        this.$router.push({
          name: 'project-specification-import',
          params: { projectId: this.projectId, jobId: job.id },
        });
        return;
      }
      if (job.type === 'images') this.openPhotos(job);
    },
    /** «Отменить» у задания в работе (ТЗ 4.1): найденное до отмены остаётся в позициях. */
    async cancelJob(job) {
      if (await model.cancelJob(job)) await model.refresh(this.projectId);
    },
    dismissJob(job) {
      model.dismissJob(job);
    },
    /** «Импортировать снова» из истории (ТЗ 6.2): панель закрывается, задание видно в индикаторе. */
    async onReimport(record) {
      if (await model.reimport(record.id)) this.historyOpen = false;
    },
    async openPhotos(job) {
      const applied = await popup.add({
        wrappable: true,
        component: SpecPhotosPopupWrappable,
        params: { job, items: this.items },
      });
      // перечитываем, только если фото действительно применяли: иначе показывать нечего
      if (applied) await model.refresh(this.projectId);
    },

    async openImport() {
      const result = await popup.add({ wrappable: true, component: SpecImportPopupWrappable });
      if (!result) return;
      await model.startJob({
        specification: `/api/specifications/${this.specification.id}`,
        type: 'import',
        files: result.fileIds.map((id) => `/api/files/${id}`),
        input: result.hint ? { hint: result.hint } : {},
      });
    },

    async openRequestDialog(items) {
      const sent = await popup.add({
        wrappable: true,
        component: SpecRequestPopupWrappable,
        params: {
          specificationId: this.specification.id,
          items,
          region: this.project?.region ?? '',
        },
      });
      this.selectedIds = [];
      // запросы меняют статусы позиций; если ничего не отправляли, перечитывать нечего
      if (!sent) return;
      await model.refresh(this.projectId);
      if (this.requestsOpen) await this.reloadRequests();
    },

    async openRequests() {
      await this.reloadRequests();
      this.requestsOpen = true;
    },
    reloadRequests() {
      return specificationApi.fetchRequests(this.specification.id)
        .then((r) => { this.requests = r.items ?? []; });
    },
    /** После «На доработку» меняются и статусы позиций, и сами запросы. */
    async onReworked() {
      await this.reloadRequests();
      await model.refresh(this.projectId);
    },

    async openSettings() {
      const specification = await popup.add({
        wrappable: true,
        component: SpecSettingsPopupWrappable,
        params: { specification: this.specification, readOnly: this.readOnly },
      });
      // окно отдаёт саму спецификацию из ответа сервера — отдельное перечитывание не нужно
      if (specification && specification.id) model.setSpecification(specification);
    },

    async onChoose({ item, offer }) {
      const result = await popup.add({
        wrappable: true,
        component: SpecSplitPopupWrappable,
        params: {
          mode: 'choose',
          item,
          offer,
          commissionPercent: this.specification.commissionPercent,
        },
      });
      if (!result) return;
      await model.chooseOffer(item.id, {
        offer: offer.id,
        customerDiscount: result.customerDiscount,
      });
    },
    async onChangeSplit({ item, offer }) {
      const result = await popup.add({
        wrappable: true,
        component: SpecSplitPopupWrappable,
        params: {
          mode: 'change',
          item,
          offer,
          commissionPercent: this.specification.commissionPercent,
        },
      });
      if (!result) return;
      await model.changeDiscount(item.id, {
        customerDiscount: result.customerDiscount,
        reason: result.reason,
      });
    },
    /**
     * «На доработку» из строки отклика (ТЗ 6.2): запрос берётся из самого отклика (`requestId`),
     * а не ищется по поставщику — у поставщика в одной спецификации может быть несколько запросов.
     */
    async onOfferRework({ item, offer }) {
      const requestId = offer.requestId ?? null;
      if (!requestId) return;
      const comment = await popup.addPrompt(
        'На доработку',
        'Комментарий поставщику обязателен',
      );
      if (!comment) return;
      const result = await apiRun(
        () => specRequestApi.rework(requestId, { items: [item.id], comment }),
        'Не удалось отправить позицию на доработку',
      );
      if (!result) return;
      await model.refresh(this.projectId);
      if (this.requestsOpen) await this.reloadRequests();
    },

    exportUrl(format, view) {
      return specificationApi.exportUrl(this.specification.id, {
        format,
        view,
        group: this.grouping === 'room' ? 'room' : 'category',
        items: this.visibleIds,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.specification-page {
  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
  }

  &__export {
    position: relative;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
  }

  &__grouping {
    display: flex;
    gap: 6px;
  }

  &__tab {
    padding: 6px 12px;
    border: 1px solid var(--color-super-light);
    border-radius: 6px;
    background: var(--color-white);
    font: inherit;
    font-size: 12px;
    cursor: pointer;

    &.active {
      border-color: var(--color-accent);
      color: var(--color-accent);
    }
  }

  &__filter {
    box-sizing: border-box;
    padding: 6px 8px;
    border: 1px solid var(--color-super-light);
    border-radius: 6px;
    font: inherit;
    font-size: 12px;
  }

  &__empty {
    padding: 40px 0;
    text-align: center;
    color: var(--color-middle-gray);
  }
}
</style>
